import React, { useEffect } from "react";
import { Button, ButtonGroup, Image } from "react-bootstrap";
import SpinnerCustom from "@/components/ui/spinner";
import type {
  IDishDetailsFormValues,
  IDishDetailsModalViewModel,
} from "@/types";
import RenderDishMarkers from "@/pages/menuItemsPage/components/renderDishMarkers";
import { DashLg, PlusLg } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import ScaleIcon from "../../../../../public/images/icons/scale.svg";
import DishPortionSelector from "@/pages/menuItemsPage/components/dishPortionSelector";
import DishSupplementsSelector from "@/pages/menuItemsPage/components/dishSupplementsSelector";
import { useFormikContext } from "formik";
import { CookingTimeBadge } from "@/components/ui/cookingTimeBadge";
import DishIcon from "@/../public/images/icons/dish-icon.svg";
import styles from "./dishDetailsModalContent.module.scss";
import { iconTintFilter } from "@/constants";

interface DishDetailsModalProps {
  viewModel: IDishDetailsModalViewModel;
  isLoading?: boolean;
}

const DishDetailsModalContent: React.FC<DishDetailsModalProps> = ({
  viewModel,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { values, submitCount, status, setFieldValue } =
    useFormikContext<IDishDetailsFormValues>();
  const invalidRequiredGroupId = status?.invalidRequiredGroupId ?? null;

  const handleDecreaseDishQuantity = () => {
    if (values.dishQuantity <= 1) return;
    setFieldValue("dishQuantity", values.dishQuantity - 1);
  };

  const handleIncreaseDishQuantity = () => {
    setFieldValue("dishQuantity", values.dishQuantity + 1);
  };

  useEffect(() => {
    if (!invalidRequiredGroupId || submitCount <= 0) return;

    document
      .getElementById(`supp-group-${invalidRequiredGroupId}`)
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  }, [submitCount, invalidRequiredGroupId]);

  const hasDishImage = Boolean(viewModel?.imageUrl);
  const imageToRender =
    viewModel?.imageUrl || viewModel?.fallbackCategoryIconUrl || DishIcon;
  const hasCookingTime = Boolean(viewModel?.cookingTime?.trim());
  const shouldShowCookingTimeBadge =
    hasCookingTime || Boolean(viewModel?.isLongCookingTime);

  if (isLoading || !viewModel) {
    return (
      <div className="d-flex justify-content-center py-5">
        <SpinnerCustom />
      </div>
    );
  }

  return (
    <>
      {imageToRender ? (
        <div
          className={`mb-5 rounded-3 ${!hasDishImage ? "bg-violet-bg" : ""}`}
        >
          {hasDishImage ? (
            <Image
              src={imageToRender}
              alt={viewModel.title}
              loading="lazy"
              decoding="async"
              className="w-100 rounded-3 object-fit-cover"
              style={{ maxHeight: 200 }}
            />
          ) : (
            <Image
              src={imageToRender}
              alt={viewModel.title}
              className="w-100 rounded-3 p-7 object-fit-contain"
              style={{
                maxHeight: 200,
                filter: iconTintFilter,
              }}
            />
          )}
        </div>
      ) : null}

      {viewModel.description ? (
        <p className="mb-2 text-grey-text">{viewModel.description}</p>
      ) : null}
      {viewModel.composition ? (
        <p className="mb-2 text-grey-text">{viewModel.composition}</p>
      ) : null}

      <RenderDishMarkers
        markers={viewModel.markers}
        allergens={viewModel.allergens}
        variant="list"
      />

      <div className="d-flex align-items-center mt-5 mb-5 mb-md-9">
        <span className={`fw-bold ${styles.minPortionPrice}`}>
          {viewModel.basicPrice}
        </span>
        {viewModel.basicVolume ? (
          <span className="d-inline-flex align-items-center ms-1 fs-14-18 text-grey-text gap-1">
            <span>/</span>
            <Image
              src={ScaleIcon}
              className="d-inline-block d-md-none"
              width={13}
              height={13}
            />
            <Image
              src={ScaleIcon}
              className="d-none d-md-inline-block"
              width={17}
              height={17}
            />
            {viewModel.basicVolume}
          </span>
        ) : null}
        {shouldShowCookingTimeBadge ? (
          <div className="d-inline-flex align-items-center ms-1 text-grey-text gap-1">
            <span className="fs-14-18">/</span>
            <CookingTimeBadge
              cookingTimeText={viewModel.cookingTime}
              isLongCookingTime={viewModel.isLongCookingTime}
            />
          </div>
        ) : null}
      </div>

      <DishPortionSelector
        portions={viewModel.portions ?? []}
        unit={viewModel.unit}
        currency={viewModel.currency}
        selectedPortionId={values.portionId ?? null}
        onChangeSelectedPortionId={(portionId) =>
          setFieldValue("portionId", portionId)
        }
      />

      <DishSupplementsSelector
        supplements={viewModel.supplements ?? []}
        currency={viewModel.currency}
        className="mt-4"
      />
      <section className="mt-8 mt-md-9">
        <h5 className="fs-16 fw-normal text-center m-0">{t("Quantity")}</h5>
        <div className="mt-2 mt-md-4 d-flex justify-content-center">
          <ButtonGroup size="sm">
            <Button
              type="button"
              variant="additional-violet"
              disabled={values.dishQuantity <= 1}
              onClick={handleDecreaseDishQuantity}
              className="rounded-1 border-0"
              style={{ width: "35px", height: "35px" }}
            >
              <DashLg size={16} color="#5842BE" />
            </Button>

            <div className="px-4 fs-16 d-flex align-items-center">
              {values.dishQuantity}
            </div>

            <Button
              type="button"
              variant="additional-violet"
              onClick={handleIncreaseDishQuantity}
              className="rounded-1 border-0"
              style={{ width: "35px", height: "35px" }}
            >
              <PlusLg size={16} color="#5842BE" />
            </Button>
          </ButtonGroup>
        </div>
      </section>
    </>
  );
};

export default DishDetailsModalContent;
